import type { ReactNode } from 'react';

import { Communication, General, Time, Users } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Size } from './types';

export const TEMPLATE_SORT_DEFAULT_VALUE = 'created';

const templateSortOptions = [
  { value: 'created', label: 'Newest', icon: <Time.Calendar /> },
  { value: 'active', label: 'Most active', icon: <General.Zap /> },
  { value: 'enrolled', label: 'Most enrolled', icon: <Users.Users01 /> },
  { value: 'response_rate', label: 'Best response rate', icon: <Communication.MessageSquare01 /> },
  { value: 'connection_rate', label: 'Best connection rate', icon: <General.Link01 /> },
  { value: 'last_run', label: 'Recently active', icon: <Time.Clock /> },
] as const;

export type TemplateSortFilterSelectProps = {
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function TemplateSortFilterSelect({
  value,
  defaultValue,
  onChange,
  placeholder = 'Sort by',
  className,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  onClear,
}: TemplateSortFilterSelectProps) {
  const selectedOption = templateSortOptions.find((o) => o.value === value);
  const icon = selectedOption?.icon ?? <General.FilterLines />;

  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={icon}
      options={[...templateSortOptions]}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
